import { useCallback, useEffect, useRef, useState } from 'react';

import {
    LAUNCH_MILESTONE_BY_TYPE,
    ORPHAN_MILESTONE_DEFINITIONS,
    deriveDueDate,
} from './milestones';

import type { MilestoneReminderValues } from './components/MilestoneCard/MilestoneCard';
import type { DeadlineMilestoneIdValue } from './milestones';
import type { DeadlineMilestoneValues } from './validation';
import type { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleMilestone } from '@/types/cycle-evaluation/CycleMilestone';
import type { ScheduleReminder } from '@/types/cycle-evaluation/CycleSchedule';

import { OFFERED_REMINDER_DAYS_BEFORE_DEADLINE } from '@/utils/schedule';

export type RemindersById = Partial<Record<DeadlineMilestoneIdValue, MilestoneReminderValues>>;

/** The three reminder pills as the screen holds them, read from what a schedule stores. */
const buildRemindersFromSchedule = (
    scheduleReminders: readonly ScheduleReminder[],
): MilestoneReminderValues => {
    // Destructured in `OFFERED_REMINDER_DAYS_BEFORE_DEADLINE`'s own [7, 3, 0] order, so a
    // change to that shared scale is the only place this mapping would need to follow.
    const [reminderSevenDays, reminderThreeDays, reminderOnDeadline] =
        OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.map((daysBeforeDeadline) =>
            scheduleReminders.some(
                (reminder) => reminder.daysBeforeDeadline === daysBeforeDeadline,
            ),
        );

    return { reminderSevenDays, reminderThreeDays, reminderOnDeadline };
};

interface RollbackRefusedValuesParams {
    values: DeadlineMilestoneValues;
    remindersById: RemindersById;
}

interface UseHydrateDeadlineValuesParams {
    isDataReady: boolean;
    orphanMilestones: CycleMilestone[];
    checkedTypes: EvaluationTypeValue[];
    cycleEvaluationQueryByType: Record<
        EvaluationTypeValue,
        ReturnType<typeof useGetCycleEvaluation>
    >;
}

/**
 * Owns the Deadlines step's `values`/`remindersById` state and their one-time hydration from the
 * group's orphan milestones and each checked type's `CycleSchedule` — pulled out of `DeadlinesPage`
 * so the hydration rules can be read on their own, away from the save handler.
 */
const useHydrateDeadlineValues = ({
    isDataReady,
    orphanMilestones,
    checkedTypes,
    cycleEvaluationQueryByType,
}: UseHydrateDeadlineValuesParams) => {
    const [values, setValues] = useState<DeadlineMilestoneValues>({});
    const [remindersById, setRemindersById] = useState<RemindersById>({});

    const hydratedRef = useRef(false);

    // Latched by the first gesture and never lowered, so a write coming back cannot report the step
    // as untouched and strand the choice made while it was in flight. The ref is what the autosave
    // effect reads without re-running; the state is what re-renders the validation, which weighs a
    // past date differently once a launched cycle has been touched.
    const hasUserEditedRef = useRef(false);
    const [hasUserEdited, setHasUserEdited] = useState(false);

    useEffect(() => {
        if (!isDataReady || hydratedRef.current) {
            return;
        }

        hydratedRef.current = true;

        const nextValues: DeadlineMilestoneValues = {};
        const nextReminders: RemindersById = {};

        for (const milestone of ORPHAN_MILESTONE_DEFINITIONS) {
            const existing = orphanMilestones.find(
                (row) => row.milestoneType === milestone.milestoneType,
            );

            nextValues[milestone.id] = {
                scheduledAt: existing?.scheduledAt ? new Date(existing.scheduledAt) : undefined,
            };
        }

        for (const type of checkedTypes) {
            const definition = LAUNCH_MILESTONE_BY_TYPE[type];
            const schedule = cycleEvaluationQueryByType[type]?.data?.cycleSchedule;

            if (!schedule) {
                continue;
            }

            const scheduledAt = schedule.scheduledAt ? new Date(schedule.scheduledAt) : undefined;

            nextValues[definition.id] = {
                scheduledAt,
                dueAt: scheduledAt
                    ? deriveDueDate({
                          scheduledAt,
                          daysAllowedToComplete: schedule.daysAllowedToComplete,
                      })
                    : undefined,
            };

            nextReminders[definition.id] = buildRemindersFromSchedule(schedule.scheduleReminders);
        }

        setValues(nextValues);
        setRemindersById(nextReminders);
    }, [isDataReady, orphanMilestones, checkedTypes, cycleEvaluationQueryByType]);

    const markDraftChanged = useCallback(() => {
        hasUserEditedRef.current = true;
        setHasUserEdited(true);
    }, []);

    const handleScheduledAtChange = useCallback(
        (id: DeadlineMilestoneIdValue, date?: Date) => {
            markDraftChanged();
            setValues((current) => ({ ...current, [id]: { ...current[id], scheduledAt: date } }));
        },
        [markDraftChanged],
    );

    const handleDueAtChange = useCallback(
        (id: DeadlineMilestoneIdValue, date?: Date) => {
            markDraftChanged();
            setValues((current) => ({ ...current, [id]: { ...current[id], dueAt: date } }));
        },
        [markDraftChanged],
    );

    const handleRemindersChange = useCallback(
        (id: DeadlineMilestoneIdValue, reminders: MilestoneReminderValues) => {
            markDraftChanged();
            setRemindersById((current) => ({ ...current, [id]: reminders }));
        },
        [markDraftChanged],
    );

    // `hasUserEditedRef` stays raised on purpose — the administrator HAS edited the step, and
    // lowering it here would strand the next gesture.
    const rollbackRefusedValues = useCallback(
        ({
            values: refusedValues,
            remindersById: refusedReminders,
        }: RollbackRefusedValuesParams) => {
            setValues((current) => ({ ...current, ...refusedValues }));
            setRemindersById((current) => ({ ...current, ...refusedReminders }));
        },
        [],
    );

    return {
        values,
        remindersById,
        hasUserEditedRef,
        hasUserEdited,
        markDraftChanged,
        handleScheduledAtChange,
        handleDueAtChange,
        handleRemindersChange,
        rollbackRefusedValues,
    };
};

export { useHydrateDeadlineValues, buildRemindersFromSchedule };
